/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCSExperiment } from 'experiments/hooks/currentABTests/useCSExperiment'
import { toAlgoliaProductHit } from 'layout/navigation/search/utils'
import { DEFAULT_LOCALE } from 'localization/enums/LocalizationEnums'
import isEmpty from 'lodash/isEmpty'
import omit from 'lodash/omit'
import Router, { useRouter } from 'next/router'
import qs from 'qs'
import { ParsedUrlQuery } from 'querystring'
import { useCallback, useEffect, useRef, useState } from 'react'
import { SearchState as AlgoliaSearchState } from 'react-instantsearch-core'
import { AlgoliaIndex, SortOption } from 'search'
import AlgoliaResults, { AlgoliaResultsNoConnect } from 'search/components/AlgoliaResults'
import AlgoliaSearchClient from 'search/components/AlgoliaSearchClient'
import AlgoliaSearchGridContext, {
  AlgoliaSearchGridContextProps,
} from 'search/components/AlgoliaSearchGridContext'
import DesktopAlgoliaSearchGridLayout, {
  ConsumerSearchDesktopGridLayout,
} from 'search/components/DesktopAlgoliaSearchGridLayout'
import MobileAlgoliaSearchGridLayout, {
  ConsumerSearchMobileGridLayout,
} from 'search/components/MobileAlgoliaSearchGridLayout'
import PageLevelInstantSearch from 'search/components/PageLevelInstantSearch'
import { ResultsState } from 'search/types/ResultState'
import GridHeader from 'shared/components/GridHeader'
import Spinner from 'shared/components/Spinner'
import { ProductSearchResponse } from 'shared/types/ConsumerSearch'
import { SearchState } from 'shared/types/Search'
import {
  getResultsState,
  searchStateToUrl,
  urlToSearchState,
} from 'shared/utils/search/algolia-utils'
import {
  ConsumerSearchFilters,
  getClientConsumerSearchFilters,
  transformAggregationsToStructuredData,
} from 'shared/utils/search/cs-utils'

export type GridConfig = {
  context: AlgoliaSearchGridContextProps
  regex?: RegExp
  indexPath?: string
}

export type GridProps = {
  /** CS search state with just base refinement lists (used in PLPs).
   * @example
   *  /adidas PLP ==> {brand_name: ['adidas']}
   *  /apparel PLP ==> {product_category: ['clothing', 'bags', 'accessories']}
   * */
  baseCSSearchState?: SearchState
  /** Algolia grid configuration */
  config: GridConfig
  /** Consumer Search results */
  consumerSearchResult?: ProductSearchResponse
  /** Consumer Search search state for the Grid  */
  consumerSearchState: SearchState
  /** Consumer Search trending results */
  consumerSearchTrendingResults?: ProductSearchResponse
  /** Description for the grid header */
  description?: string
  /** No results state for Algolia layout (i.e. trending results) */
  noResultsState?: ResultsState
  /** Algolia initial search state */
  queryParamSearchState: SearchState
  /** Results state for Algolia layout */
  resultsState?: ResultsState
  /** Darwin group from SSR for Consumer Search experiment */
  ssrCSDarwinGroup?: string // TODO: remove once CS Experiment is finished
  /** Title for the grid */
  title: string
}

const Grid = ({
  config,
  consumerSearchResult,
  consumerSearchState,
  consumerSearchTrendingResults,
  description,
  noResultsState,
  queryParamSearchState,
  resultsState,
  ssrCSDarwinGroup,
  title,
}: GridProps) => {
  const { context } = config
  const [algoliaSearchState, setAlgoliaSearchState] = useState(queryParamSearchState)
  const [algoliaResultsState, setAlgoliaResultsState] = useState(resultsState)
  const [noAlgoliaResultsState, setNoAlgoliaResultsState] = useState(noResultsState)
  const [modelFilters, setModelFilters] = useState(consumerSearchResult?.allFilters || {})
  const [searchState, setSearchState] = useState<SearchState>(consumerSearchState)
  const [currentConsumerSearchResult, setCurrentConsumerSearchResult] =
    useState(consumerSearchResult)
  const router = useRouter()
  const refQueryString = useRef(getQueryString(router.query))
  const currentSlug = Array.isArray(router.query.slug)
    ? router.query.slug.join('/')
    : router.query.slug || ''

  /** Algolia Instant Search state change handler */
  const handleSearchStateChange = (newSearchState: AlgoliaSearchState) => {
    replaceRouterHref(newSearchState, router.locale || DEFAULT_LOCALE)
    setAlgoliaSearchState(isEmpty(newSearchState) ? queryParamSearchState : newSearchState)
  }

  // CS EXPERIMENT
  const { isAssigning, isConsumerSearchActive, isCSExperimentEnabled, isSSRGroupDifferent } =
    useCSExperiment({ shouldStartExperiment: true, ssrCSDarwinGroup })

  // Fetch Algolia search data
  const fetchAlgoliaSearchData = useCallback(
    async (qsSearchState: SearchState, searchState: SearchState) => {
      const {
        resultsState: algoliaResultsState,
        trendingResultsState: algoliaTrendingResultsState,
      } = await getResultsState(qsSearchState, searchState, 30)

      setAlgoliaResultsState(algoliaResultsState)
      setNoAlgoliaResultsState(algoliaTrendingResultsState)
    },
    [],
  )

  // Fetch Consumer Search data
  const fetchClientConsumerSearchData = useCallback(
    async (currentSearchParams: ConsumerSearchFilters) => {
      const url = '/api/product-search?' + new URLSearchParams(currentSearchParams).toString()
      const { data, error } = await fetch(url, {
        method: 'GET',
      })
        .then(async (res) => {
          return res.json()
        })
        .catch(() => {
          return {
            data: {},
            error: 'error fetching data',
            total: 0,
          }
        })
      // here if router query changes then make call to consumer search api
      const newConsumerSearchResult = {
        error: error || null,
        data: toAlgoliaProductHit(data?.productsList || []),
        total: data?.totalResults || 0,
        aggregation: {
          filters: transformAggregationsToStructuredData(data?.aggregationValuesList || []),
        },
      }

      setCurrentConsumerSearchResult(newConsumerSearchResult)
    },
    [],
  )

  // Consumer Search fetch data effect
  useEffect(() => {
    const isNewQueryString = getQueryString(router.query) !== refQueryString.current
    if (isConsumerSearchActive && searchState && (isSSRGroupDifferent || isNewQueryString)) {
      refQueryString.current = getQueryString(router.query)
      const currentSearchParams = getClientConsumerSearchFilters(
        consumerSearchState || {},
        (router.query as Record<string, string>) || {},
        { silhouettes: true },
      )
      void fetchClientConsumerSearchData(currentSearchParams)
    }
    // Only fetch from client when the CS experiment is active and the search state or router query changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConsumerSearchActive, isSSRGroupDifferent, searchState.refinementList])

  // Algolia search fetch data effect
  useEffect(() => {
    if (
      isConsumerSearchActive === false &&
      isSSRGroupDifferent &&
      queryParamSearchState &&
      consumerSearchState
    ) {
      fetchAlgoliaSearchData(queryParamSearchState, consumerSearchState)
    }
  }, [
    isSSRGroupDifferent,
    isConsumerSearchActive,
    queryParamSearchState,
    consumerSearchState,
    fetchAlgoliaSearchData,
  ])

  // Update CS search state with refinementList from URL query
  useEffect(() => {
    const qsRefinementList = urlToSearchState(router.query).refinementList
    setSearchState((prevSearchState) => ({ ...prevSearchState, refinementList: qsRefinementList }))
  }, [router.query])

  // Update CS search state when consumerSearchState changes (i.e. nav between PLPs)
  useEffect(() => {
    setSearchState(consumerSearchState)
  }, [consumerSearchState])

  // Update current consumer search result when consumerSearchResult changes (i.e. nav between PLPs)
  useEffect(() => {
    setCurrentConsumerSearchResult(consumerSearchResult)
    setModelFilters(consumerSearchResult?.allFilters || {})
  }, [consumerSearchResult])

  // Update algolia search state when queryParamSearchState changes (i.e. nav between PLPs)
  useEffect(() => {
    setAlgoliaSearchState(queryParamSearchState)
  }, [queryParamSearchState])

  // Log experiment exposure if enabled
  // no longer log exposure for grids
  //
  // useEffect(() => {
  //   if (isCSExperimentEnabled) {
  //     logCSExposure()
  //   }
  // }, [isCSExperimentEnabled, logCSExposure])

  // ======== RENDER SEARCH RESULTS ========

  if (isCSExperimentEnabled) {
    if (isAssigning) {
      return (
        <>
          <GridHeader description={`${description}`} title={title} />
          <Spinner showSpinner={isAssigning} />
        </>
      )
    }

    // return CS layout
    if (isConsumerSearchActive) {
      return (
        <AlgoliaResultsNoConnect
          isAlgolia={false}
          noResultsState={consumerSearchTrendingResults?.data as any}
          searchResults={currentConsumerSearchResult?.data as any}
          searchState={searchState}
        >
          <GridHeader description={description} title={title} />
          {modelFilters && (
            <ConsumerSearchMobileGridLayout
              consumerResults={currentConsumerSearchResult}
              defaultRefinement={router.query.sortBy || searchState.index || AlgoliaIndex.Relevance}
              hiddenFilters={config.context.hiddenFilters}
              initialFilters={modelFilters}
              initialSearchState={searchState}
              nbHits={currentConsumerSearchResult?.total}
              sortConfig={sortConfig()}
            />
          )}
          {modelFilters && (
            <ConsumerSearchDesktopGridLayout
              consumerResults={currentConsumerSearchResult}
              defaultRefinement={router.query.sortBy || searchState.index || AlgoliaIndex.Relevance}
              hiddenFilters={config.context.hiddenFilters}
              initialFilters={modelFilters}
              initialSearchState={searchState}
              nbHits={currentConsumerSearchResult?.total}
              sortConfig={sortConfig()}
            />
          )}
        </AlgoliaResultsNoConnect>
      )
    }
  }

  // return Algolia layout
  return (
    <AlgoliaSearchGridContext.Provider value={context}>
      <PageLevelInstantSearch
        key={currentSlug}
        resultsState={algoliaResultsState}
        searchState={algoliaSearchState}
        filters={context.filters}
        indexName={context.index || ''}
        onSearchStateChange={handleSearchStateChange}
        hitsPerPage={30}
        searchClient={AlgoliaSearchClient}
        ruleContexts={context?.ruleContexts}
      >
        <AlgoliaResults noResultsState={noAlgoliaResultsState as any}>
          <GridHeader title={title} description={description} />
          <MobileAlgoliaSearchGridLayout
            sortConfig={sortConfig()}
            defaultRefinement={context.index || AlgoliaIndex.Relevance}
            hiddenFilters={context?.hiddenFilters}
          />
          <DesktopAlgoliaSearchGridLayout
            sortConfig={sortConfig()}
            defaultRefinement={context.index || AlgoliaIndex.Relevance}
            hiddenFilters={context?.hiddenFilters}
          />
        </AlgoliaResults>
      </PageLevelInstantSearch>
    </AlgoliaSearchGridContext.Provider>
  )
}
export default Grid

function getQueryString(qs: ParsedUrlQuery) {
  const q = omit(qs, 'slug') // omit slug as it's only relevant for NextJS routing
  return `${new URLSearchParams(q as Record<string, string>).toString()}`
}

function replaceRouterHref(searchState: AlgoliaSearchState, locale: string) {
  const urlParams = searchStateToUrl(searchState)
  const href = isEmpty(urlParams)
    ? `${window.location.pathname}`
    : `${window.location.pathname}?${qs.stringify(urlParams)}`

  Router.replace(href, href, { shallow: true, locale })
}

function sortConfig() {
  return Object.keys(SortOption()).map((option) => ({
    value: AlgoliaIndex[option as keyof typeof AlgoliaIndex],
    label: SortOption()[option as keyof ReturnType<typeof SortOption>],
  }))
}
